import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Sun, Moon, LockKeyhole, Menu, X } from "lucide-react";
import { PHONE_TEL, isOpenNow, scrollToSection } from "@/lib/hours";

const LINKS = [
  { id: "carte", label: "La Carte", testid: "nav-link-carte" },
  { id: "manifesto", label: "Manifesto", testid: "nav-link-manifesto" },
  { id: "horaires", label: "Horaires", testid: "nav-link-horaires" },
  { id: "contact", label: "Contact", testid: "nav-link-contact" },
];

export default function Header() {
  const [open, setOpen] = useState(isOpenNow());
  const [menuOpen, setMenuOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem("bf_theme") === "dark");

  useEffect(() => {
    const t = setInterval(() => setOpen(isOpenNow()), 60000);
    return () => clearInterval(t);
  }, []);

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    if (next) {
      document.documentElement.setAttribute("data-theme", "dark");
      localStorage.setItem("bf_theme", "dark");
    } else {
      document.documentElement.removeAttribute("data-theme");
      localStorage.setItem("bf_theme", "cream");
    }
  };

  return (
    <motion.header
      data-testid="main-header-nav"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 lg:px-8">
        <button
          data-testid="nav-brand-logo"
          onClick={() => scrollToSection("hero")}
          className="flex items-center"
        >
          <img src="/images/palmi-logo.png" alt="PALMI Pizza Club" className="logo-mark h-11 w-auto shrink-0 sm:h-12" />
        </button>

        <nav className="hidden items-center gap-8 md:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => scrollToSection(l.id)}
              className="link-underline text-sm font-medium text-dim hover:text-foreground transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-dim hover:text-terra hover:border-terra transition-colors md:hidden"
            aria-label="Menu"
          >
            {menuOpen ? <X size={17} /> : <Menu size={17} />}
          </button>
          <span
            data-testid="nav-status-badge"
            className={`badge-tag hidden sm:inline-flex ${open ? "badge-veg" : "badge-hot"}`}
          >
            {open ? "Ouvert" : "Fermé"}
          </span>
          <button
            data-testid="nav-theme-toggle"
            onClick={toggleTheme}
            className="hidden h-9 w-9 items-center justify-center rounded-full border border-border text-dim hover:text-terra hover:border-terra transition-colors sm:flex"
            aria-label="Changer de thème"
          >
            {dark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <Link
            data-testid="nav-admin-access-button"
            to="/admin"
            className="hidden h-9 w-9 items-center justify-center rounded-full border border-border text-dim hover:text-terra hover:border-terra transition-colors sm:flex"
            aria-label="Accès admin"
          >
            <LockKeyhole size={15} />
          </Link>
          <a data-testid="nav-phone-cta-button" href={PHONE_TEL} className="btn-primary hidden !py-2.5 !px-5 text-sm sm:inline-flex">
            <Phone size={15} /> Commander
          </a>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="absolute left-0 right-0 top-[72px] border-b border-border bg-background/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col px-5 py-2">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-${l.testid}`}
                  onClick={() => {
                    setMenuOpen(false);
                    scrollToSection(l.id);
                  }}
                  className="border-b border-border/50 py-3.5 text-left font-medium last:border-0"
                >
                  {l.label}
                </button>
              ))}
              <a data-testid="mobile-nav-phone-cta" href={PHONE_TEL} className="btn-primary my-3 justify-center text-sm">
                <Phone size={15} /> Appeler pour commander
              </a>
              <div className="flex items-center gap-3 pb-3">
                <button
                  data-testid="mobile-theme-toggle"
                  onClick={toggleTheme}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-dim hover:text-terra hover:border-terra transition-colors"
                  aria-label="Changer de thème"
                >
                  {dark ? <Sun size={16} /> : <Moon size={16} />}
                </button>
                <Link
                  data-testid="mobile-admin-access"
                  to="/admin"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-dim hover:text-terra hover:border-terra transition-colors"
                  aria-label="Accès admin"
                >
                  <LockKeyhole size={15} />
                </Link>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
